import { Group, ScrollArea, SimpleGrid, Stack } from '@mantine/core';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import { integrationList } from '@repo/common/constants/integration/integrations';
import type {
	BuiltinIntegrationSpec,
	IntegrationGroup,
} from '@repo/common/interfaces/integrationSpec';
import { Button, Text, Title } from '@repo/foundations';
import { memo, useMemo } from 'react';
import { useIntegrationList } from '../../../api';
import { AuthCard } from '../../../components/Auth/AuthCard';
import { AuthFooter } from '../../../components/Auth/AuthLayouts/AuthFooter';
import { shouldShowIntegrationWithFeatureFlag } from '../../../components/Integration/AddIntegrationModal/AddIntegrationModal.helpers';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { IntegrationButton } from './IntegrationButton';
import { OnboardingStepper } from './OnboardingStepper';

export const SecodaLiteIntegrationListStep = memo(
	({
		onBack,
		selectedIntegration,
		onChooseIntegration,
	}: {
		onBack: () => void;
		selectedIntegration?: string;
		onChooseIntegration: (integrationId: string) => void;
	}) => {
		const allFeatureFlags = useFeatureFlags();
		const { data: memberEnv } = useSuspenseApiMemberEnv({});
		const { data: existingIntegrations } = useIntegrationList({});

		const integrations = useMemo(
			() =>
				integrationList
					.filter(
						(integration) =>
							!integration.isExtension &&
							!integration.hidden &&
							(memberEnv?.SLACK_ENABLED || integration.type !== 'slack') &&
							integration.type !== 'marketplace' &&
							integration.secodaEnabled &&
							!integration.secodaCoreEnabled
					)
					.filter((integration) =>
						shouldShowIntegrationWithFeatureFlag({
							integration,
							featureFlags: allFeatureFlags,
							existingIntegrations: existingIntegrations,
						})
					)
					.reduce(
						(acc, integration) => ({
							...acc,
							[integration.group]: [
								...(acc[integration.group] || []),
								integration,
							],
						}),
						{} as Record<IntegrationGroup, BuiltinIntegrationSpec[]>
					),
			[allFeatureFlags, memberEnv, existingIntegrations]
		);

		const groups = Object.keys(integrations);

		return (
			<AuthCard width={800} p="2xl">
				<Stack spacing="xl">
					<Stack spacing={0}>
						<Title>What data would you like to work with?</Title>
					</Stack>
					<ScrollArea.Autosize mah={428}>
						<Stack spacing="xl">
							{groups.map((group) => (
								<Stack key={group} spacing="md">
									<Text weight="bold">{group}</Text>
									<SimpleGrid cols={3} spacing="md">
										{integrations[group as IntegrationGroup].map(
											(integration) => (
												<IntegrationButton
													key={integration.type}
													integration={integration}
													onClick={() => onChooseIntegration(integration.type)}
												/>
											)
										)}
									</SimpleGrid>
								</Stack>
							))}
						</Stack>
					</ScrollArea.Autosize>
				</Stack>
				<AuthFooter>
					<Group position="apart" py="md" px="2xl">
						<OnboardingStepper step={1} totalSteps={5} />
						<Group spacing="xs" position="right">
							<Button variant="default" onClick={onBack}>
								Back
							</Button>
							<Button
								onClick={() => onChooseIntegration('')}
								variant="primary"
								disabled={!selectedIntegration}
							>
								Continue
							</Button>
						</Group>
					</Group>
				</AuthFooter>
			</AuthCard>
		);
	}
);
